import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import StatisticsCard from "../../components/statistics-card";
import { StatItem } from "../../components/mobile/types";
import { useIsMobile } from "../../hooks/use-is-mobile";
import { cn } from "../../lib/utils";

export const ReportStats: React.FC<{ items: StatItem[]; desktopClassName: string }> = ({
  items,
  desktopClassName,
}) => {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return (
      <div className={desktopClassName}>
        {items.map((item) => (
          <StatisticsCard
            key={item.label}
            label={item.label}
            value={item.value}
            description={item.description}
            icon={item.icon}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2.5">
      {items.map((item, index) => (
        <div
          key={item.label}
          className={cn(
            "flex min-w-0 flex-col justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-3.5 shadow-[0_1px_2px_rgba(13,14,19,0.04)]",
            items.length % 2 === 1 && index === items.length - 1 && "col-span-2",
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <span className="text-xs font-medium leading-tight text-gray-500 line-clamp-2">
              {item.label}
            </span>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary-50 [&_svg]:h-4 [&_svg]:w-4">
              {item.icon}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-lg font-bold leading-tight font-jakarta text-gray-900 [overflow-wrap:anywhere]">
              {item.value}
            </p>
            {item.description && (
              <p className="mt-1 text-[11px] font-medium leading-snug text-emerald-600 [overflow-wrap:anywhere]">
                {item.description}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export const ReportSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <section className="space-y-2">
        <h2 className="px-1 text-sm font-semibold text-gray-900">{title}</h2>
        {children}
      </section>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
};

export interface BreakdownRow {
  label: string;
  values: Array<{ label: string; value: number; color: string }>;
}

interface MobileBarBreakdownProps {
  title: string;
  rows: BreakdownRow[];
  /** Render each row's values as one segmented bar (values are parts of a whole). */
  stacked?: boolean;
  formatValue?: (value: number) => string;
}

const defaultFormat = (value: number) => value.toLocaleString();

export const MobileBarBreakdown: React.FC<MobileBarBreakdownProps> = ({
  title,
  rows,
  stacked = false,
  formatValue = defaultFormat,
}) => {
  const legend = rows[0]?.values ?? [];
  const max = Math.max(
    1,
    ...rows.map((row) =>
      stacked
        ? row.values.reduce((sum, v) => sum + v.value, 0)
        : Math.max(0, ...row.values.map((v) => v.value)),
    ),
  );
  const showLegend = legend.length > 1;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {showLegend && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
          {legend.map((item) => (
            <span key={item.label} className="flex items-center gap-1.5 text-[11px] text-gray-500">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
      )}
      <div className="mt-4 space-y-4">
        {rows.map((row) => {
          const total = row.values.reduce((sum, v) => sum + v.value, 0);
          return (
            <div key={row.label} className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-[13px] font-medium text-gray-800">
                  {row.label}
                </span>
                {(stacked || row.values.length === 1) && (
                  <span className="shrink-0 text-[13px] font-semibold text-gray-900">
                    {formatValue(total)}
                  </span>
                )}
              </div>
              {stacked ? (
                <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-100">
                  {row.values.map((v) => (
                    <div
                      key={v.label}
                      className="h-full"
                      style={{ width: `${(v.value / max) * 100}%`, backgroundColor: v.color }}
                    />
                  ))}
                </div>
              ) : (
                row.values.map((v) => (
                  <div key={v.label} className="flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.max((v.value / max) * 100, v.value > 0 ? 2 : 0)}%`,
                          backgroundColor: v.color,
                        }}
                      />
                    </div>
                    {row.values.length > 1 && (
                      <span className="w-24 shrink-0 truncate text-right text-[11px] text-gray-500">
                        {formatValue(v.value)}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
